// AIT-529 — /invite/:slug, the affiliate invite consent screen.
//
// A client opens an affiliate's managed link (hookmyapp.com/i/<slug> redirects
// here). The page shows who invited them and what continuing means (the
// affiliate becomes an admin on the client's account), records consent, then
// hands off to the WorkOS login/signup round-trip. Lives OUTSIDE
// <ProtectedRoute>: the client has no account yet.
//
// Design: this page is seen by external clients, so it follows the marketing
// site's DESIGN.md language (dark #1C1E21 surface, Meta Blue glow + pill CTA,
// Montserrat, 20px card radius) — NOT the internal app shell.
//
// Task 7: wired to the real GET /invite/:slug + POST /invite/:slug/consent
// (Task 4). On Continue: consent is recorded, {slug, consentId} is persisted
// to sessionStorage (invite-storage.ts) so it survives the WorkOS round-trip,
// then signIn() hands off to /invite/connect, which fires the grant in the
// background while the client picks a channel.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Loader2 } from 'lucide-react';
import { useSession } from '@/contexts/session-context';
import { setInviteContext } from '@/lib/invite-storage';
import { PageShell } from './page-shell';

const API_URL = import.meta.env.VITE_API_URL ?? '';

// Consent-screen copy can't sanely render an unbounded affiliate-supplied
// name -- cap at 80 chars. Fix round 1 (Ruling 3): NEVER ellipsize on this
// consent screen -- "gives Bright Horizons Marke… full control" reads as a
// trust problem, not a cosmetic overflow. Wrap instead (break-words below).
const DISPLAY_NAME_MAX = 80;

interface InviteInfo {
  displayName: string;
}

export function InvitePage() {
  const { slug } = useParams();
  const { signIn } = useSession();
  const [invite, setInvite] = useState<InviteInfo | null | 'loading'>('loading');
  const [submitting, setSubmitting] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!slug) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- terminal not-active state, not a render cascade (same pattern as workspace-context.tsx:242)
      setInvite(null);
      return;
    }
    void (async () => {
      try {
        const res = await fetch(`${API_URL}/invite/${encodeURIComponent(slug)}`);
        if (cancelled) return;
        if (!res.ok) {
          setInvite(null);
          return;
        }
        const data = (await res.json()) as InviteInfo;
        if (!cancelled) setInvite(data);
      } catch {
        if (!cancelled) setInvite(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Fix round 1 (Important 2): no fpr replaceState here. Task 6's /i/<slug>
  // redirect already carries ?fpr onto this page, and re-deriving it from
  // the GET response races the already-drained fpr.js queue on a direct
  // landing. Task 6's redirect owns attribution end to end.

  if (invite === 'loading') {
    return (
      <PageShell>
        <Loader2
          role="status"
          aria-label="Loading invite"
          className="relative z-10 h-8 w-8 animate-spin text-white/70"
        />
      </PageShell>
    );
  }

  if (!invite) {
    return (
      <PageShell>
        <p role="alert" className="relative z-10 max-w-sm text-center text-white/70">
          This invite link is not active. Ask the person who sent it for a new one.
        </p>
      </PageShell>
    );
  }

  const displayName = invite.displayName.slice(0, DISPLAY_NAME_MAX);

  const handleContinue = async () => {
    if (!slug) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/invite/${encodeURIComponent(slug)}/consent`, {
        method: 'POST',
      });
      if (!res.ok) {
        setError('Something went wrong. Please try again.');
        setSubmitting(false);
        return;
      }
      const data = (await res.json()) as { consentId: string };
      setInviteContext({ slug, consentId: data.consentId });
      signIn({ returnTo: '/invite/connect' });
    } catch {
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <div className="relative z-10 w-full max-w-md rounded-[20px] border border-white/10 bg-white/[0.04] px-8 py-10 text-center shadow-[0_12px_28px_rgba(0,0,0,0.2),0_2px_4px_rgba(0,0,0,0.1)] backdrop-blur-sm">
        <img src="/logo-mark.svg" alt="" aria-hidden="true" className="mx-auto mb-6 h-14 w-auto" />

        <h1 className="text-3xl font-semibold tracking-tight text-white">
          <span className="break-words">{displayName}</span> invited you to HookMyApp
        </h1>

        <div className="mt-5 space-y-2 text-left text-lg leading-relaxed text-white/90">
          <p>They are requesting admin permission to manage your organization.</p>
          <p>This permission gives them full control over your account.</p>
          <p className="text-white/70">You can remove this access anytime.</p>
        </div>

        <label className="mt-6 flex cursor-pointer items-start justify-start gap-3 text-left text-base text-white/90">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-1 h-5 w-5 shrink-0 cursor-pointer accent-[#0064E0]"
          />
          <span>I agree to give {displayName} full control of my organization</span>
        </label>

        {error ? (
          <p role="alert" className="mt-4 text-sm text-red-300">
            {error}
          </p>
        ) : null}

        <button
          type="button"
          onClick={() => void handleContinue()}
          disabled={submitting || !agreed}
          className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-[100px] bg-[#0064E0] px-8 py-4 text-base font-medium text-white transition hover:bg-[#0143B5] active:bg-[#004BB9] disabled:opacity-60"
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Continue'}
        </button>
      </div>
    </PageShell>
  );
}
